import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import WorkspaceLayout from "@/components/WorkspaceLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UserCog, Plus, Trash2, Power, PowerOff, X, ShieldCheck } from "lucide-react";

export default function HRStaffPage() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ roles: [], permission_keys: [], templates: {} });
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const m = (await api.get("/org/hr-staff/meta")).data;
      setMeta(m);
      const r = (await api.get("/org/hr-staff")).data;
      setRows(r.rows);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const setActive = async (sid, active) => {
    try {
      await api.patch(`/org/hr-staff/${sid}`, { active });
      toast.success(active ? "Activated" : "Deactivated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (sid, email) => {
    if (!confirm(`Remove ${email}?`)) return;
    try { await api.delete(`/org/hr-staff/${sid}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <WorkspaceLayout
      subtitle="HR workspace"
      title="HR Staff & Roles"
      actions={
        <Button onClick={() => { setEditing(null); setShowModal(true); }} className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="hr-staff-new">
          <Plus className="w-4 h-4" /> Add HR staff
        </Button>
      }
    >
      <Card className="rounded-sm border-ec-line">
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="p-10 text-center text-ec-ink-soft" data-testid="hr-staff-empty">
              <UserCog className="w-10 h-10 mx-auto mb-3 opacity-40" />
              No HR staff yet. Add your first team member to delegate HR work.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-ec-mute">
                <tr className="text-left">
                  <Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Permissions</Th><Th>Status</Th><Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} className="border-t border-ec-line hover:bg-ec-mute/50" data-testid={`hr-staff-row-${s.id}`}>
                    <Td className="font-semibold">{s.full_name}</Td>
                    <Td className="font-mono-plex text-xs text-ec-ink-soft">{s.email}</Td>
                    <Td><Badge className="rounded-sm bg-ec-mute border-ec-line text-ec-ink">{s.hr_role}</Badge></Td>
                    <Td><PermissionDots permissions={s.permissions} keys={meta.permission_keys} /></Td>
                    <Td>{s.active ? <Badge className="rounded-sm bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30">Active</Badge> : <Badge className="rounded-sm bg-ec-error/10 text-ec-error border-ec-error/30">Inactive</Badge>}</Td>
                    <Td className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <button onClick={() => { setEditing(s); setShowModal(true); }} className="p-1.5 border border-ec-line rounded-sm hover:bg-ec-mute" data-testid={`hr-staff-edit-${s.id}`}>
                          <UserCog className="w-3 h-3" />
                        </button>
                        <button onClick={() => setActive(s.id, !s.active)} className="p-1.5 border border-ec-line rounded-sm hover:bg-ec-mute" data-testid={`hr-staff-toggle-${s.id}`}>
                          {s.active ? <PowerOff className="w-3 h-3" /> : <Power className="w-3 h-3" />}
                        </button>
                        <button onClick={() => del(s.id, s.email)} className="p-1.5 border border-ec-error/30 text-ec-error rounded-sm hover:bg-ec-error/10" data-testid={`hr-staff-delete-${s.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {showModal && (
        <StaffModal
          meta={meta}
          editing={editing}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
        />
      )}
    </WorkspaceLayout>
  );
}

function Th({ children, className = "" }) { return <th className={`px-4 py-3 eyebrow ${className}`}>{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>; }

function PermissionDots({ permissions = {}, keys = [] }) {
  const granted = keys.filter((k) => permissions[k]).length;
  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-0.5">
        {keys.map((k) => (
          <span key={k} title={k.replace(/_/g, " ")}
            className={`w-2 h-2 rounded-full ${permissions[k] ? "bg-ec-emerald" : "bg-ec-line"}`} />
        ))}
      </div>
      <span className="text-[10px] font-mono-plex text-ec-ink-soft">{granted}/{keys.length}</span>
    </div>
  );
}

function StaffModal({ meta, editing, onClose, onSaved }) {
  const init = editing || {
    email: "", full_name: "", hr_role: "HR Manager", branch: "", department: "",
    custom_permissions: null,
  };
  const [f, setF] = useState({ ...init, permissions: editing?.permissions || meta.templates?.[init.hr_role] || {} });
  const [advanced, setAdvanced] = useState(false);

  const setRole = (role) => {
    const tpl = meta.templates?.[role] || {};
    setF({ ...f, hr_role: role, permissions: { ...tpl } });
  };
  const togglePerm = (k) => setF({ ...f, permissions: { ...f.permissions, [k]: !f.permissions?.[k] } });

  const save = async () => {
    try {
      const body = {
        full_name: f.full_name, email: f.email, hr_role: f.hr_role,
        custom_permissions: f.permissions, branch: f.branch || null, department: f.department || null,
      };
      if (editing) {
        const { custom_permissions, hr_role, branch, department } = body;
        await api.patch(`/org/hr-staff/${editing.id}`, { hr_role, custom_permissions, branch, department });
      } else {
        await api.post("/org/hr-staff", body);
      }
      toast.success(editing ? "Updated" : "Added");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="hr-staff-modal">
      <div className="bg-white rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-ec-line" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-ec-line px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-ec-emerald" /><h3 className="font-display font-bold text-lg">{editing ? "Edit HR Staff" : "Add HR Staff"}</h3></div>
          <button onClick={onClose} className="text-ec-ink-soft hover:text-ec-ink" data-testid="hr-staff-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          {!editing && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="eyebrow">Full Name</Label><Input value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} className="rounded-sm mt-2" data-testid="hr-staff-name" /></div>
              <div><Label className="eyebrow">Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-sm mt-2" data-testid="hr-staff-email" /></div>
            </div>
          )}
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1">
              <Label className="eyebrow">Role</Label>
              <Select value={f.hr_role} onValueChange={setRole}>
                <SelectTrigger className="rounded-sm mt-2" data-testid="hr-staff-role-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta.roles || []).map((r) => <SelectItem key={r} value={r} data-testid={`hr-staff-role-${r}`}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="eyebrow">Branch</Label><Input value={f.branch || ""} onChange={(e) => setF({ ...f, branch: e.target.value })} className="rounded-sm mt-2" data-testid="hr-staff-branch" /></div>
            <div><Label className="eyebrow">Department</Label><Input value={f.department || ""} onChange={(e) => setF({ ...f, department: e.target.value })} className="rounded-sm mt-2" data-testid="hr-staff-dept" /></div>
          </div>

          <div className="border border-ec-line rounded-sm">
            <button type="button" onClick={() => setAdvanced(!advanced)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-ec-mute" data-testid="hr-staff-advanced-toggle">
              <span className="eyebrow">Custom Permission Builder</span>
              <span className="text-xs text-ec-ink-soft">{advanced ? "Hide" : "Show"}</span>
            </button>
            {advanced && (
              <div className="border-t border-ec-line p-4 grid sm:grid-cols-2 gap-2" data-testid="hr-staff-permissions-grid">
                {(meta.permission_keys || []).map((k) => (
                  <label key={k} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-sm border border-ec-line hover:bg-ec-mute cursor-pointer" data-testid={`hr-staff-perm-${k}`}>
                    <input type="checkbox" checked={!!f.permissions?.[k]} onChange={() => togglePerm(k)} className="w-4 h-4 accent-ec-emerald" />
                    <span>{k.replace(/_/g, " ")}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-ec-line px-5 py-3 flex justify-end gap-2">
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink" data-testid="hr-staff-save">{editing ? "Save changes" : "Add staff"}</Button>
        </div>
      </div>
    </div>
  );
}
